import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { addAmiriFont, addArabicText, isAmiriFontLoaded } from '../utils/amiri-font.js';

const ArabicPDFExample = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');

  const generateSamplePDF = async () => {
    setIsLoading(true);
    setStatus('إنشاء ملف PDF...');

    try {
      // Create a sample Arabic PDF manually
      const doc = new jsPDF();
      
      // Add Amiri font
      await addAmiriFont(doc);
      
      // Add Arabic title
      addArabicText(doc, 'مرحباً بكم في تطبيق اللوجستيات', 200, 30, {
        fontSize: 20,
        align: 'center',
        color: [0, 0, 139] // Dark blue
      });
      
      // Add Arabic subtitle
      addArabicText(doc, 'نظام إدارة النقل والشحن', 200, 50, {
        fontSize: 14,
        align: 'center',
        color: [105, 105, 105] // Gray
      });
      
      // Add Arabic content
      addArabicText(doc, 'هذا مثال على استخدام الخط العربي الأميري في ملفات PDF. يمكن استخدام هذا الخط لعرض النصوص العربية بشكل جميل ومقروء.', 190, 80, {
        fontSize: 12,
        align: 'right',
        maxWidth: 150,
        lineHeight: 1.5
      });
      
      // Save the PDF
      doc.save('arabic-sample.pdf');
      setStatus('تم إنشاء الملف بنجاح!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      setStatus('حدث خطأ في إنشاء الملف');
    } finally {
      setIsLoading(false);
    }
  };

  const generateCustomPDF = async () => {
    setIsLoading(true);
    setStatus('إنشاء ملف PDF مخصص...');

    try {
      const doc = new jsPDF();
      
      // Load Amiri font
      const fontLoaded = await addAmiriFont(doc);
      
      if (!fontLoaded) {
        setStatus('فشل في تحميل الخط العربي');
        return;
      }

      // Add custom Arabic content
      addArabicText(doc, 'تقرير اللوجستيات اليومي', 200, 30, {
        fontSize: 18,
        align: 'center',
        color: [0, 100, 0] // Green
      });

      addArabicText(doc, `التاريخ: ${new Date().toLocaleDateString('ar-SA')}`, 190, 60, {
        fontSize: 12,
        align: 'right'
      });

      // Add a table-like structure
      const data = [
        { label: 'عدد الشحنات المسلمة', value: '25' },
        { label: 'عدد الشحنات المؤجلة', value: '3' },
        { label: 'إجمالي المبيعات', value: '15,450 ريال' },
        { label: 'عدد السائقين النشطين', value: '8' },
      ];

      let yPosition = 90;
      data.forEach((item) => {
        addArabicText(doc, `${item.label}: ${item.value}`, 190, yPosition, {
          fontSize: 11,
          align: 'right'
        });
        yPosition += 20;
      });

      // Add footer
      addArabicText(doc, 'شركة اللوجستيات المحدودة', 200, 250, {
        fontSize: 10,
        align: 'center',
        color: [128, 128, 128] // Gray
      });

      // Save the PDF
      doc.save(`logistics-report-${Date.now()}.pdf`);
      setStatus('تم إنشاء التقرير بنجاح!');
    } catch (error) {
      console.error('Error generating custom PDF:', error);
      setStatus('حدث خطأ في إنشاء التقرير');
    } finally {
      setIsLoading(false);
    }
  };

  const testFontLoading = async () => {
    setIsLoading(true);
    setStatus('اختبار تحميل الخط...');

    try {
      const doc = new jsPDF();
      const fontLoaded = await addAmiriFont(doc);
      
      if (fontLoaded && isAmiriFontLoaded(doc)) {
        setStatus('تم تحميل الخط العربي بنجاح ✓');
      } else {
        setStatus('فشل في تحميل الخط العربي ✗');
      }
    } catch (error) {
      console.error('Error testing font:', error);
      setStatus('حدث خطأ في اختبار الخط');
    } finally {
      setIsLoading(false);
    }
  };

  const testInvoiceStyle = async () => {
    setIsLoading(true);
    setStatus('اختبار نمط الفاتورة...');

    try {
      const doc = new jsPDF();
      
      // Load Arabic font
      const fontLoaded = await addAmiriFont(doc);
      
      if (!fontLoaded) {
        setStatus('فشل في تحميل الخط العربي');
        return;
      }

      // Test logo loading with a sample image
      try {
        const img = new Image();
        // Remove crossOrigin to avoid CORS issues
        // img.crossOrigin = 'anonymous'; // This causes CORS errors
        
        // Use a sample logo URL for testing
        const testLogoUrl = 'https://via.placeholder.com/150x100/2563eb/ffffff?text=LOGO';
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = testLogoUrl;
        });
        
        // Add test logo
        doc.addImage(img, 'PNG', 20, 20, 40, 25);
        console.log('Test logo added successfully');
      } catch (logoError) {
        console.error('Test logo failed:', logoError);
      }

      // Test invoice-style Arabic text
      doc.setFontSize(28);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(41, 98, 255);
      doc.text('INVOICE / فاتورة', doc.internal.pageSize.getWidth() / 2, 60, { align: 'center' });

      // Test client info with Arabic - including the specific issue mentioned by user
      addArabicText(doc, 'نمق', 20, 90, {
        fontSize: 14,
        align: 'left'
      });
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('^ Should read "نمق" (not backwards as "قمن")', 50, 90);
      doc.setTextColor(0, 0, 0);

      addArabicText(doc, 'شركة الخدمات اللوجستية المحدودة', 20, 110, {
        fontSize: 12,
        align: 'left'
      });

      addArabicText(doc, 'الرياض، المملكة العربية السعودية', 20, 125, {
        fontSize: 11,
        align: 'left'
      });

      // Test description
      addArabicText(doc, 'خدمات النقل والشحن من الرياض إلى جدة', 25, 150, {
        fontSize: 10,
        align: 'left'
      });

      // Test footer
      addArabicText(doc, 'شكراً لتعاملكم معنا', doc.internal.pageSize.getWidth() / 2, 250, {
        fontSize: 12,
        align: 'center',
        color: [105, 105, 105]
      });

      // Save test PDF
      doc.save('invoice-style-test.pdf');
      setStatus('تم إنشاء اختبار نمط الفاتورة بنجاح! 🎉');
    } catch (error) {
      console.error('Error generating invoice test:', error);
      setStatus('حدث خطأ في اختبار نمط الفاتورة');
    } finally {
      setIsLoading(false);
    }
  };

  const testCompanyLogo = async () => {
    setIsLoading(true);
    setStatus('اختبار شعار الشركة...');

    try {
      // Test company logo loading functionality
      console.log('Testing company logo loading...');
      
      // Get current user - this might not work in this context, so we'll use a different approach
      console.log('Testing company logo loading...');
      
      // Instead, let's just test if we can load common logo formats
      const testUrls = [
        'https://via.placeholder.com/200x120/0066cc/ffffff?text=Company+Logo',
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iIzAwNjZjYyIvPjx0ZXh0IHg9IjEwMCIgeT0iNjUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkNvbXBhbnkgTG9nbzwvdGV4dD48L3N2Zz4='
      ];

      for (const [index, testUrl] of testUrls.entries()) {
        try {
          console.log(`Testing logo URL ${index + 1}:`, testUrl.substring(0, 50) + '...');
          
          const img = new Image();
          // Remove crossOrigin to avoid CORS issues
          // img.crossOrigin = 'anonymous'; // This causes CORS errors
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              console.log(`Logo ${index + 1} loaded successfully, dimensions:`, img.width, 'x', img.height);
              resolve();
            };
            img.onerror = (error) => {
              console.error(`Logo ${index + 1} loading failed:`, error);
              reject(error);
            };
            img.src = testUrl;
          });
          
          // Create test PDF with this logo
          const doc = new jsPDF();
          doc.addImage(img, 'PNG', 20, 20 + (index * 30), 40, 25);
          doc.text(`Test Logo ${index + 1}`, 70, 30 + (index * 30));
          
          if (index === testUrls.length - 1) {
            doc.save('logo-test.pdf');
            setStatus('تم اختبار الشعارات بنجاح! ✅');
          }
          
        } catch (error) {
          console.error(`Error with test logo ${index + 1}:`, error);
        }
      }
      
    } catch (error) {
      console.error('Error testing company logo:', error);
      setStatus('حدث خطأ في اختبار الشعار');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center">
        Arabic PDF Generator Example
        <br />
        <span className="text-lg text-gray-600">مثال على إنشاء ملفات PDF العربية</span>
      </h1>

      <div className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">تعليمات الاستخدام</h2>
          <ul className="text-sm space-y-1 text-gray-700">
            <li>• تأكد من وجود ملف Amiri-Regular.ttf في مجلد public/fonts/</li>
            <li>• اختبر تحميل الخط أولاً قبل إنشاء ملفات PDF</li>
            <li>• استخدم "اختبار الشعار" لاختبار تحميل الشعارات في PDF</li>
            <li>• استخدم "اختبار الفاتورة" لاختبار النص العربي في الفواتير</li>
            <li>• يمكنك إنشاء ملف PDF نموذجي أو مخصص</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <button
            onClick={testFontLoading}
            disabled={isLoading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            {isLoading ? 'جاري الاختبار...' : 'اختبار الخط'}
          </button>

          <button
            onClick={testCompanyLogo}
            disabled={isLoading}
            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            {isLoading ? 'جاري الاختبار...' : 'اختبار الشعار'}
          </button>

          <button
            onClick={testInvoiceStyle}
            disabled={isLoading}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            {isLoading ? 'جاري الاختبار...' : 'اختبار الفاتورة'}
          </button>

          <button
            onClick={generateSamplePDF}
            disabled={isLoading}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            {isLoading ? 'جاري الإنشاء...' : 'ملف نموذجي'}
          </button>

          <button
            onClick={generateCustomPDF}
            disabled={isLoading}
            className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            {isLoading ? 'جاري الإنشاء...' : 'تقرير مخصص'}
          </button>
        </div>

        {status && (
          <div className={`p-3 rounded ${
            status.includes('نجاح') || status.includes('✓') 
              ? 'bg-green-100 text-green-800' 
              : status.includes('خطأ') || status.includes('✗')
              ? 'bg-red-100 text-red-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {status}
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Usage in Code:</h3>
          <pre className="text-sm bg-gray-800 text-green-400 p-3 rounded overflow-x-auto">
{`import { addAmiriFont, addArabicText } from './utils/amiri-font.js';

const generatePDF = async () => {
  const doc = new jsPDF();
  
  // Load Amiri font
  await addAmiriFont(doc);
  
  // Add Arabic text
  addArabicText(doc, 'النص العربي', 190, 50, {
    fontSize: 16,
    align: 'right',
    color: [0, 0, 0]
  });
  
  doc.save('arabic-document.pdf');
};`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ArabicPDFExample; 